import { cacheExchange } from "@urql/exchange-graphcache";
import { generatedCacheKeys } from "./cache-keys.generated";

/**
 * Creates the cache exchange configuration for URQL
 * Handles optimistic updates and cache invalidation for mutations
 */
export const createCacheConfig = () => {
  return cacheExchange({
    // Keys configuration is auto-generated from schema.graphql
    // See: scripts/generate-cache-keys.ts
    keys: generatedCacheKeys,
    possibleTypes: {
      SchedulerItem: ["NewsAlertType", "ScheduledBriefingType"],
    },
    // Optimistic updates configuration
    optimistic: {
      deleteFeedArticle: (args, cache, _info) => {
        // Get the article IDs that will be deleted
        const input = args.input as { feedArticleId?: string[] };
        const deletedIds = input?.feedArticleId || [];

        // Invalidate individual article entities immediately
        for (const id of deletedIds) {
          cache.invalidate({ __typename: "FeedArticleType", id });
        }

        // Invalidate all feedArticles query results
        const fields = cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "feedArticles");

        for (const field of fields) {
          cache.invalidate("Query", "feedArticles", field.arguments);
        }

        // Return optimistic response
        return {
          __typename: "SuccessResponse",
          message: "Deleting articles...",
        };
      },
      createFeedSavedView: (_args, cache, _info) => {
        // Invalidate all feedSavedViews query results immediately
        const fields = cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "feedSavedViews");

        for (const field of fields) {
          cache.invalidate("Query", "feedSavedViews", field.arguments);
        }

        // Return optimistic response
        return {
          __typename: "FeedSavedViewType",
        };
      },
      updateFeedSavedView: (_args, cache, _info) => {
        // Invalidate all feedSavedViews query results immediately
        const fields = cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "feedSavedViews");

        for (const field of fields) {
          cache.invalidate("Query", "feedSavedViews", field.arguments);
        }

        // Return optimistic response
        return {
          __typename: "FeedSavedViewType",
        };
      },
      deleteFeedSavedView: (_args, cache, _info) => {
        // Invalidate all feedSavedViews query results immediately
        const fields = cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "feedSavedViews");

        for (const field of fields) {
          cache.invalidate("Query", "feedSavedViews", field.arguments);
        }

        // Return optimistic response
        return {
          __typename: "SuccessResponse",
          message: "Deleting saved view...",
        };
      },
      renameStory: (_args, cache, _info) => {
        // Invalidate all newsFeedDynamicSidebar query results immediately
        const fields = cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "newsFeedDynamicSidebar");

        for (const field of fields) {
          cache.invalidate("Query", "newsFeedDynamicSidebar", field.arguments);
        }

        // Return optimistic response
        return {
          __typename: "StoryType",
        };
      },
      deleteStory: (_args, cache, _info) => {
        // Invalidate all newsFeedDynamicSidebar query results immediately
        const fields = cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "newsFeedDynamicSidebar");

        for (const field of fields) {
          cache.invalidate("Query", "newsFeedDynamicSidebar", field.arguments);
        }

        // Return optimistic response
        return {
          __typename: "SuccessResponse",
          message: "Deleting cluster...",
        };
      },
      mergeStories: (_args, cache, _info) => {
        // Invalidate all newsFeedDynamicSidebar query results immediately
        const fields = cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "newsFeedDynamicSidebar");

        for (const field of fields) {
          cache.invalidate("Query", "newsFeedDynamicSidebar", field.arguments);
        }

        // Return optimistic response
        return {
          __typename: "StoryType",
        };
      },
    },
    // Cache updates configuration - runs after server confirms
    updates: {
      Mutation: {
        deleteFeedArticle: (result, args, cache, _info) => {
          const deletionResult = result.deleteFeedArticle as {
            __typename?: string;
          };

          // Only update cache if deletion was successful
          if (deletionResult?.__typename === "SuccessResponse") {
            const input = args.input as { feedArticleId?: string[] };
            const deletedIds = input?.feedArticleId || [];

            // Invalidate individual article entities
            for (const id of deletedIds) {
              cache.invalidate({ __typename: "FeedArticleType", id });
            }

            // Invalidate all feedArticles query results
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "feedArticles");

            for (const field of fields) {
              cache.invalidate("Query", "feedArticles", field.arguments);
            }
          }
          // If error, the optimistic invalidation is auto-rolled back
        },
        createFeedSavedView: (result, _args, cache, _info) => {
          const createdSavedView = result.createFeedSavedView as {
            __typename?: string;
          };

          // Only invalidate cache if creation was successful
          if (createdSavedView?.__typename === "FeedSavedViewType") {
            // Invalidate all feedSavedViews query results
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "feedSavedViews");

            for (const field of fields) {
              cache.invalidate("Query", "feedSavedViews", field.arguments);
            }
          }
        },
        updateFeedSavedView: (result, _args, cache, _info) => {
          const updatedSavedView = result.updateFeedSavedView as {
            __typename?: string;
          };

          // Only invalidate cache if update was successful
          if (updatedSavedView?.__typename === "FeedSavedViewType") {
            // Invalidate all feedSavedViews query results
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "feedSavedViews");

            for (const field of fields) {
              cache.invalidate("Query", "feedSavedViews", field.arguments);
            }
          }
        },
        deleteFeedSavedView: (result, _args, cache, _info) => {
          const deletionResult = result.deleteFeedSavedView as {
            __typename?: string;
          };

          // Only invalidate cache if deletion was successful
          if (deletionResult?.__typename === "SuccessResponse") {
            // Invalidate all feedSavedViews query results
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "feedSavedViews");

            for (const field of fields) {
              cache.invalidate("Query", "feedSavedViews", field.arguments);
            }
          }
        },
        renameStory: (result, _args, cache, _info) => {
          const renameResult = result.renameStory as {
            __typename?: string;
          };

          // Only invalidate cache if rename was successful
          if (renameResult?.__typename === "StoryType") {
            // Invalidate all newsFeedDynamicSidebar query results
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "newsFeedDynamicSidebar");

            for (const field of fields) {
              cache.invalidate(
                "Query",
                "newsFeedDynamicSidebar",
                field.arguments,
              );
            }
          }
        },
        deleteStory: (result, _args, cache, _info) => {
          const deletionResult = result.deleteStory as {
            __typename?: string;
          };

          // Only invalidate cache if deletion was successful
          if (deletionResult?.__typename === "SuccessResponse") {
            // Invalidate all newsFeedDynamicSidebar query results
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "newsFeedDynamicSidebar");

            for (const field of fields) {
              cache.invalidate(
                "Query",
                "newsFeedDynamicSidebar",
                field.arguments,
              );
            }
          }
        },
        mergeStories: (result, _args, cache, _info) => {
          const mergeResult = result.mergeStories as {
            __typename?: string;
          };

          // Only invalidate cache if merge was successful
          if (mergeResult?.__typename === "StoryType") {
            // Invalidate all newsFeedDynamicSidebar query results
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "newsFeedDynamicSidebar");

            for (const field of fields) {
              cache.invalidate(
                "Query",
                "newsFeedDynamicSidebar",
                field.arguments,
              );
            }
          }
        },
        createNewsAlert: (result, _args, cache, _info) => {
          const createdNewsAlert = result.createNewsAlert as {
            __typename?: string;
          };

          if (createdNewsAlert?.__typename === "NewsAlertType") {
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "schedulerItems");

            for (const field of fields) {
              cache.invalidate("Query", "schedulerItems", field.arguments);
            }
          }
        },
        updateNewsAlert: (result, _args, cache, _info) => {
          const updatedNewsAlert = result.updateNewsAlert as {
            __typename?: string;
          };

          if (updatedNewsAlert?.__typename === "NewsAlertType") {
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "schedulerItems");

            for (const field of fields) {
              cache.invalidate("Query", "schedulerItems", field.arguments);
            }
          }
        },
        deleteNewsAlert: (result, _args, cache, _info) => {
          const deletionResult = result.deleteNewsAlert as {
            __typename?: string;
          };

          if (deletionResult?.__typename === "SuccessResponse") {
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "schedulerItems");

            for (const field of fields) {
              cache.invalidate("Query", "schedulerItems", field.arguments);
            }
          }
        },
        createScheduledBriefing: (result, _args, cache, _info) => {
          const createdBriefing = result.createScheduledBriefing as {
            __typename?: string;
          };

          if (createdBriefing?.__typename === "ScheduledBriefingType") {
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "schedulerItems");

            for (const field of fields) {
              cache.invalidate("Query", "schedulerItems", field.arguments);
            }
          }
        },
        updateScheduledBriefing: (result, _args, cache, _info) => {
          const updatedBriefing = result.updateScheduledBriefing as {
            __typename?: string;
          };

          if (updatedBriefing?.__typename === "ScheduledBriefingType") {
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "schedulerItems");

            for (const field of fields) {
              cache.invalidate("Query", "schedulerItems", field.arguments);
            }
          }
        },
        deleteScheduledBriefing: (result, _args, cache, _info) => {
          const deletionResult = result.deleteScheduledBriefing as {
            __typename?: string;
          };

          if (deletionResult?.__typename === "SuccessResponse") {
            const fields = cache
              .inspectFields("Query")
              .filter((field) => field.fieldName === "schedulerItems");

            for (const field of fields) {
              cache.invalidate("Query", "schedulerItems", field.arguments);
            }
          }
        },
        deleteBriefingDefinition: (result, args, cache, _info) => {
          const deletionResult = result.deleteBriefingDefinition as {
            __typename?: string;
          };

          if (deletionResult?.__typename === "SuccessResponse") {
            cache.invalidate({
              __typename: "BriefingDefinition",
              id: args.id as string,
            });

            // Drop cached definition lookups and briefings table pages.
            const fields = cache
              .inspectFields("Query")
              .filter(
                (field) =>
                  field.fieldName === "briefingDefinitions" ||
                  field.fieldName === "briefings",
              );

            for (const field of fields) {
              cache.invalidate("Query", field.fieldName, field.arguments);
            }
          }
        },
      },
    },
  });
};
